// =============================================================================
// NS Small B2B - Constants
// =============================================================================

// -----------------------------------------------------------------------------
// Team Codes
// -----------------------------------------------------------------------------

export const TEAM_CODES = ['A', 'B', 'C', 'D'] as const;
export type TeamCode = (typeof TEAM_CODES)[number];

// -----------------------------------------------------------------------------
// Customer Types
// -----------------------------------------------------------------------------

export const CUSTOMER_TYPES = [
    'ร้านค้าช่วง-ร้านวัสดุก่อสร้าง',
    'ร้านค้าช่วง-ร้านวัสดุอื่นๆ',
    'ผู้รับเหมาก่อสร้าง',
    'ผู้รับเหมาติดตั้ง (แอร์, ปั๊มน้ำ, ประตูหน้าต่าง ฯลฯ)',
    'ช่างอิสระ (ช่างประปา, ช่างไฟ, ช่างแอร์ ฯลฯ)',
    'โครงการจัดสรร/หมู่บ้าน',
    'โรงแรม/รีสอร์ท/โรงพยาบาล',
    'โรงงาน/อุตสาหกรรม',
    'หน่วยงานราชการ',
    'สถาบันการศึกษา',
    'ลูกค้ารายย่อย/เจ้าของบ้าน',
] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];

// -----------------------------------------------------------------------------
// Product Groups
// -----------------------------------------------------------------------------

export const PRODUCT_GROUPS = [
    'โครงสร้าง',
    'เครื่องมือช่าง',
    'ฮาร์ดแวร์',
    'เกษตรสวน-ประปา/ปั้มน้ำ',
    'ประตู-หน้าต่าง/ลูกบิด',
    'สีและเคมีภัณฑ์/คิ้วไม้',
    'เครื่องเขียน/กิ๊ฟช็อป',
    'เครื่องใช้ไฟฟ้า',
    'เครื่องครัวบานซิงค์',
    'เครื่องใช้ภายในบ้าน',
    'เฟอร์นิเจอร์/เครื่องนอน',
    'สุขภัณฑ์/กระเบื้อง',
    'อุปกรณ์ไฟฟ้า/โคมไฟตกแต่ง',
] as const;
export type ProductGroup = (typeof PRODUCT_GROUPS)[number];

// -----------------------------------------------------------------------------
// Followup Action Types
// -----------------------------------------------------------------------------

export const FOLLOWUP_ACTION_TYPES = [
    'ติดตามราคาและสินค้า',
    'ส่งใบเสนอราคา',
    'วัดหน้างาน',
    'นัดส่งสินค้า',
    'ปิดการขาย',
    'แก้ปัญหาหลังการขายหรือติดตั้ง',
] as const;
export type FollowupActionType = (typeof FOLLOWUP_ACTION_TYPES)[number];

// -----------------------------------------------------------------------------
// Catalog Items (hardcoded DoHome catalogs)
// -----------------------------------------------------------------------------

export interface CatalogItem {
    id: string;
    name: string;
    category: string;
    url: string;
}

export const CATALOG_ITEMS: CatalogItem[] = [
    {
        id: 'catalog-1',
        name: 'DoHome - สวน',
        category: 'สวน',
        url: 'https://dohome.sharepoint.com/:b:/s/DOHOMEeBook/EYxxxxxxxxxxx',
    },
    {
        id: 'catalog-2',
        name: 'DoHome - โครงสร้าง',
        category: 'โครงสร้าง',
        url: 'https://dohome.sharepoint.com/:b:/s/DOHOMEeBook/EYxxxxxxxxxxx',
    },
    {
        id: 'catalog-3',
        name: 'DoHome - เครื่องมือช่าง',
        category: 'เครื่องมือช่าง',
        url: 'https://dohome.sharepoint.com/:b:/s/DOHOMEeBook/EYxxxxxxxxxxx',
    },
    {
        id: 'catalog-4',
        name: 'DoHome - อุปกรณ์ไฟฟ้า',
        category: 'ไฟฟ้า',
        url: 'https://dohome.sharepoint.com/:b:/s/DOHOMEeBook/EYxxxxxxxxxxx',
    },
    {
        id: 'catalog-5',
        name: 'DoHome - เครื่องใช้ไฟฟ้า',
        category: 'เครื่องใช้ไฟฟ้า',
        url: 'https://dohome.sharepoint.com/:b:/s/DOHOMEeBook/EYxxxxxxxxxxx',
    },
];

// -----------------------------------------------------------------------------
// UI & Branding
// -----------------------------------------------------------------------------

export const BRAND_COLORS = {
    primary: '#F97316',
    background: '#FFFFFF',
    text: '#4F4F4F',
    primaryLight: '#FED7AA',
    primaryDark: '#EA580C',
} as const;

// -----------------------------------------------------------------------------
// Defaults
// -----------------------------------------------------------------------------

export const DEFAULTS = {
    photoRetentionDays: 45,
    maxPhotosPerVisit: 8,
    maxPhotoSizeMB: 8,
    maxNotesLength: 3000,
    siteMatchDistanceMeters: 50,
    timezone: 'Asia/Bangkok',
} as const;

// -----------------------------------------------------------------------------
// System Settings Keys
// -----------------------------------------------------------------------------

export const SETTINGS_KEYS = {
    photoRetentionDays: 'photo_retention_days',
    photoCleanupLastRun: 'photo_cleanup_last_run',
    sitesBackfillLastRun: 'sites_backfill_last_run',
    memberQrUrl: 'member_qr_url',
} as const;

// -----------------------------------------------------------------------------
// Tab Configuration
// -----------------------------------------------------------------------------

export interface TabConfig {
    id: string;
    label: string;
    icon: string;
    roles: ('ADMIN' | 'EXECUTIVE' | 'MANAGER' | 'EMPLOYEE')[];
    hideFor?: ('ADMIN' | 'EXECUTIVE' | 'MANAGER' | 'EMPLOYEE')[];
}

export const TABS: TabConfig[] = [
    {
        id: 'dashboard',
        label: 'ภาพรวม',
        icon: '📊',
        roles: ['ADMIN', 'EXECUTIVE', 'MANAGER'],
    },
    {
        id: 'visit-form',
        label: 'ส่งข้อมูลหน้างาน',
        icon: '📝',
        roles: ['ADMIN', 'EXECUTIVE', 'MANAGER', 'EMPLOYEE'],
    },
    {
        id: 'visits-list',
        label: 'รายการหน้างาน',
        icon: '📋',
        roles: ['ADMIN', 'EXECUTIVE', 'MANAGER'],
    },
    {
        id: 'map',
        label: 'แผนที่ลูกค้า',
        icon: '🗺️',
        roles: ['ADMIN', 'EXECUTIVE', 'MANAGER'],
    },
    {
        id: 'followup',
        label: 'ติดตามลูกค้า',
        icon: '🔁',
        roles: ['ADMIN', 'EXECUTIVE', 'MANAGER'],
    },
    {
        id: 'catalog',
        label: 'แคตตาล็อก',
        icon: '📚',
        roles: ['ADMIN', 'MANAGER', 'EMPLOYEE'],
        hideFor: ['EXECUTIVE'],
    },
    {
        id: 'member',
        label: 'สมัครสมาชิก',
        icon: '👥',
        roles: ['ADMIN', 'EXECUTIVE', 'MANAGER', 'EMPLOYEE'],
    },
    {
        id: 'profile',
        label: 'โปรไฟล์',
        icon: '👤',
        roles: ['ADMIN', 'EXECUTIVE', 'MANAGER', 'EMPLOYEE'],
    },
    {
        id: 'admin',
        label: 'จัดการระบบ',
        icon: '⚙️',
        roles: ['ADMIN', 'EXECUTIVE'],
    },
];
